/**
 * adminApi.ts — SPINEX Predict Admin API layer
 *
 * Fixes applied vs original:
 *  1. Token race condition: _accessToken was read from localStorage at module
 *     load time but never updated when the interceptor cleared it on 401.
 *     Fixed by always reading/writing through setAdminToken().
 *  2. Refresh token rotation: the original had no refresh logic so any token
 *     expiry caused a silent redirect. We now queue concurrent 401 requests
 *     while a single /auth/refresh call is in flight.
 *  3. SUBSCRIPTION_PLANS was exported as a mutable const array — replaced with
 *     Object.freeze to prevent accidental mutation.
 *  4. ListParams used loose `string` for all values; tightened to typed unions.
 *  5. AdminSlip.selections typed as unknown[] — tightened to Selection[].
 *  6. Removed duplicate COLORS index in CountryPieChart (was series[5] twice).
 *  7. adminApi stats cards in Affiliate/Referral pages built raw inline instead
 *     of using the shared StatCard — no change needed in API layer but noted.
 *  8. Added missing /admin/users endpoint (GET /admin/users) for completeness.
 */

import axios, { type AxiosInstance } from 'axios'

// ─── Token persistence ──────────────────────────────────────────────────────
const TOKEN_KEY = 'spinex_admin_token'
const REFRESH_KEY = 'spinex_admin_refresh'

let _accessToken: string | null = localStorage.getItem(TOKEN_KEY)
let _refreshToken: string | null = localStorage.getItem(REFRESH_KEY)
let _refreshPromise: Promise<string | null> | null = null

export function setAdminToken(access: string | null, refresh?: string | null) {
  _accessToken = access
  if (access) localStorage.setItem(TOKEN_KEY, access)
  else localStorage.removeItem(TOKEN_KEY)

  if (refresh !== undefined) {
    _refreshToken = refresh
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
    else localStorage.removeItem(REFRESH_KEY)
  }
}

export function getAdminToken(): string | null {
  return _accessToken
}

// ─── 401 / unauthorised callback ────────────────────────────────────────────
let _onUnauthorized: (() => void) | null = null
export function setOnUnauthorized(fn: (() => void) | null) {
  _onUnauthorized = fn
}

// ─── Shared Axios instance ─────────────────────────────────────────────────
export const adminAxios: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'https://api.spinexpredict.com/api/v1',
  withCredentials: true,
  timeout: 30_000,
})

// Request: attach Bearer token
adminAxios.interceptors.request.use((cfg) => {
  if (_accessToken) cfg.headers.Authorization = `Bearer ${_accessToken}`
  return cfg
})

// Response: handle 401 with refresh-token rotation
adminAxios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config
    const isRefreshRequest = typeof original?.url === 'string' && original.url.includes('/auth/refresh')

    if (err.response?.status === 401 && original && !original._retry && !isRefreshRequest) {
      original._retry = true

      // If no refresh token, log out immediately
      if (!_refreshToken) {
        setAdminToken(null, null)
        _onUnauthorized?.()
        return Promise.reject(err)
      }

      // Deduplicate concurrent refresh calls
      if (!_refreshPromise) {
        _refreshPromise = adminAxios
          .post<{
            success: boolean
            data: { accessToken: string; refreshToken: string }
          }>('/auth/refresh', { refreshToken: _refreshToken })
          .then((res) => {
            const { accessToken, refreshToken } = res.data.data
            setAdminToken(accessToken, refreshToken)
            return accessToken
          })
          .catch(() => {
            setAdminToken(null, null)
            _onUnauthorized?.()
            return null
          })
          .finally(() => {
            _refreshPromise = null
          })
      }

      const newToken = await _refreshPromise
      if (!newToken) return Promise.reject(err)

      original.headers.Authorization = `Bearer ${newToken}`
      return adminAxios(original)
    }

    return Promise.reject(err)
  },
)

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  phone_number?: string
  role: string
  country_code: string
  language: string
  odds_format: string
  risk_level: string
  is_verified: boolean
  is_suspended: boolean
  subscription_tier?: string
  subscription_status?: string
  subscription_expires_at?: string
  slip_count?: number
  created_at: string
}

export interface SlipSelection {
  fixtureId: string
  league?: string
  homeTeam?: string
  awayTeam?: string
  market: string
  selection: string
  confidence: number
  odds?: number
  result?: string | null
}

export interface AdminSlip {
  id: string
  userId: string
  riskLevel: string
  combinedOdds: number
  confidenceScore: number
  expectedValue: number
  selections: SlipSelection[]
  status: SlipStatus
  isPublished?: boolean
  generatedBy?: string
  locale?: string
  createdAt: string
}

export type SlipStatus = 'draft' | 'pending' | 'won' | 'lost' | 'void' | 'cashout' | 'archived'

export interface League {
  id: number
  name: string
  country: string
  logo_url?: string
  logoUrl?: string
  is_active?: boolean
  isActive?: boolean
  /** API returns regions[] but the League model uses a tier int — keep both */
  regions?: string[]
  tier?: number
  confidence_threshold?: number
  confidenceThreshold?: number
  available_markets?: string[]
  availableMarkets?: string[]
  api_football_id?: number
  apiFootballId?: number
}

export interface KPI {
  dau: number
  slips_today: number
  avg_confidence: number
  conversion_rate: number
  total_users: number
}

export interface ChartPoint {
  date: string
  value: number
}

// ─── Admin audit log ─────────────────────────────────────────────────────────
export interface AuditEntry {
  id: number
  adminId: string
  adminEmail: string
  action: string
  targetType: string
  targetId: string
  summary: string
  metadata?: string // JSON string
  ip: string
  userAgent?: string
  createdAt: string
}

// ─── Ops status (Settings → Ops tile) ────────────────────────────────────────
export interface SystemStatus {
  football_budget: {
    remaining: number
    daily_max: number
    updated_at: string
  }
  last_free_odds_pass: {
    priced: number
    considered: number
    not_found: number
    matched_no_markets: number
    resolve_errors: number
    updated_at: string
  }
  last_prediction_run: {
    fixtures_considered: number
    predictions_written: number
    engine_skipped: number
    ml_called: number
    ml_agreed: number
    errors: number
    skip_reasons: Record<string, number> | null
    total_run_ms: number
    updated_at: string
  }
  /** Slips sitting 'pending' longer than the SLA — a grading-outage alarm. */
  stuck_slips?: {
    count: number
    oldest_pending_hours: number
    sla_hours: number
  }
}

// ─── Contact inbox (public contact form → /admin/contact) ───────────────────
export interface ContactMessage {
  id: number
  name: string
  email: string
  subject: string
  message: string
  status: 'new' | 'read' | 'replied'
  createdAt: string
}

// ─── Refund requests (7-day money-back → /admin/refunds) ────────────────────
export interface RefundRequest {
  id: number
  userId: string
  userEmail: string
  subscriptionId: string
  amountKobo: number
  reason: string
  status: 'pending' | 'approved' | 'rejected'
  adminNote?: string
  processedAt?: string
  createdAt: string
  updatedAt: string
}

export interface AdminMeta {
  total: number
  page: number
  perPage?: number
  totalPages?: number
  per_page?: number
  total_pages?: number
}

// ─── Subscription ────────────────────────────────────────────────────────────
// 'none' = unsubscribed (no access). There is no free plan.
export type Interval = 'none' | 'weekly' | 'monthly' | 'annual'

export interface SubscriptionPlan {
  name: string
  interval: Interval
  price: number
  paystack_code: string
  description: string
  days: number
}

export const SUBSCRIPTION_PLANS: readonly SubscriptionPlan[] = Object.freeze([
  { name: 'Weekly',  interval: 'weekly',  price: 6_000,  paystack_code: 'spinex_weekly', description: 'Full access for 1 week', days: 7   },
  { name: 'Monthly', interval: 'monthly', price: 20_000, paystack_code: 'spinex_monthly',description: 'Full access for 1 month',days: 30  },
  { name: 'Annual',  interval: 'annual',  price: 200_000,paystack_code: 'spinex_annual', description: 'Full access for 1 year', days: 365 },
])

// ─── Affiliate types ─────────────────────────────────────────────────────────
export interface Affiliate {
  id: string
  name: string
  email: string
  code: string
  tier: 'Standard' | 'Pro' | 'VIP'
  clicks: number
  signups: number
  conversions: number
  earnings: number
  status: 'active' | 'pending' | 'suspended'
  joined_at: string
}

export interface AffiliateConversion {
  id: string
  date: string
  affiliate: string
  affiliate_id: string
  referred_user: string
  amount: number
  commission: number
  status: 'confirmed' | 'pending' | 'refunded'
}

export interface AffiliatePayout {
  id: string
  affiliate: string
  affiliate_id: string
  amount: number
  method: string
  requested_at: string
  status: 'pending' | 'processing' | 'paid' | 'rejected'
}

export interface AffiliateSettings {
  standard_rate: number
  pro_rate: number
  vip_rate: number
  min_payout: number
}

// ─── Referral types ───────────────────────────────────────────────────────────
export interface Referrer {
  id: string
  user: string
  email: string
  code: string
  clicks: number
  signups: number
  paid: number
  credits_earned: number
  status: 'active' | 'suspended' | 'pending'
  joined_at: string
}

export interface LeaderboardEntry {
  rank: number
  user: string
  referrals: number
  revenue_generated: number
  credits_earned: number
}

export interface ReferralSettings {
  reward_amount: number
  max_rewards_per_user: number
  min_spend: number
  enabled: boolean
}

// ─── SaaS analytics types ─────────────────────────────────────────────────────
export interface MrrPoint        { month: string; mrr: number; target: number }
export interface ChurnPoint      { month: string; rate: number }
export interface ChurnTrendPoint { month: string; cancelled: number; retained: number; paused: number; downgraded: number }
export interface ChurnAnalytics  {
  churnByReason: Record<string, number>
  saveOfferEffectiveness: Record<string, number>
  monthlyTrend: ChurnTrendPoint[]
}
export interface LtvCacPoint     { month: string; ltv: number; cac: number; ratio: number }
export interface CohortMonth     { month: string; m1: number; m2: number; m3: number; m4: number; m5: number; m6: number; cohort_size: number }
export interface RevenueSource   { source: string; signups: number; paid_users: number; revenue: number; pct_of_total: number }
export interface TrialConversionData { rate: number; trials: number; converted: number }

// ─── WinBack types ────────────────────────────────────────────────────────────
export type ChurnReason  = 'pricing' | 'features' | 'support' | 'competitor' | 'inactive' | 'other'
export type WinBackStatus = 'not-contacted' | 'emailed' | 'responded' | 'recovered' | 'lost'

export interface ChurnedUser {
  id: string
  name: string
  email: string
  interval: string
  churn_date: string
  reason: ChurnReason
  days_since_churn: number
  status: WinBackStatus
  last_contact_date?: string
}

export interface EmailTemplate {
  id: string
  day: number
  name: string
  subject: string
  body: string
  discount: string
}

export interface CampaignMetric {
  template_id: string
  sent: number
  opened: number
  clicked: number
  recovered: number
  revenue: number
}

// ─── Settings types ───────────────────────────────────────────────────────────
export interface FeatureFlag {
  id: string
  name: string
  description: string
  enabled: boolean
  category: string
  /** Email of the admin who last flipped this flag. */
  updatedBy?: string
  updatedAt?: string
}

// ─── Typed query params ────────────────────────────────────────────────────────
export type UserStatus    = 'active' | 'suspended' | 'pending'
export type UserRole      = 'user' | 'admin' | 'superadmin'
export type RiskLevel     = 'low' | 'medium' | 'high'
export type ChartInterval = '7' | '30' | '90'

interface ListParams {
  page?: number
  per_page?: number
  search?: string
  country_code?: string
  role?: UserRole
  status?: UserStatus | SlipStatus
  risk_level?: RiskLevel
  interval?: string
  verified?: boolean
  /** Restrict to rows created in the last N days (7 | 30 | 90). */
  days?: number
}

// ─── Paginated response envelope ─────────────────────────────────────────────
interface Paginated<T> {
  data: T[]
  meta: { total: number; page: number; per_page: number; total_pages: number }
}

// ─── API ──────────────────────────────────────────────────────────────────────
export type PredictionVisibility = 'paid' | 'both'

export interface AdminPrediction {
  id: string
  fixtureId: string
  market: string
  selection: string
  confidence: number      // 0..1
  confidencePct: number   // 0..100 convenience
  probability: number
  odds: number
  reasoning: string
  /** Pipeline lifecycle: 'active' | 'expired' | 'overridden' */
  status: string
  visibility: PredictionVisibility
  source: string          // 'admin' | 'model'
  createdBy?: string
  wasCorrect?: boolean | null
  createdAt: string
  updatedAt?: string
}

export interface AdminPredictionFixture {
  id: string
  league: string
  country?: string
  homeTeam: string
  awayTeam: string
  matchDate: string
  status: string
  homeForm?: string
  awayForm?: string
  homeOdds: number
  drawOdds: number
  awayOdds: number
  over25Odds: number
  under25Odds: number
  bttsYesOdds: number
  bttsNoOdds: number
  hasOdds: boolean
  predictions: AdminPrediction[]
}

export interface ManualPredictionInput {
  fixtureId: string
  market: string
  selection: string
  confidence: number   // PERCENTAGE 1..100
  odds?: number
  reasoning?: string
  visibility: PredictionVisibility
}

export interface FixturesDiag {
  scheduler?: {
    running: boolean
    jobs?: Array<{ name: string; nextRun: string; error?: string }>
    note?: string
  }
  predictions?: { total: number; active: number }
  syncLogs?: unknown
  [key: string]: unknown
}

export interface Calibration {
  cells: {
    marketClass: string
    bucket: number
    band: string
    samples: number
    correct: number
    meanConfidence: number
    observedPct: number
    delta: number
  }[]
  overall: { graded: number; correct: number }
  minSamples: number
  holdDays: number
}

export interface Insights {
  funnel: { step: string; label: string; visitors: number }[]
  survey: {
    opened: number
    responses: number
    emailsCaptured: number
    reasons: { label: string; count: number }[]
    prices: { label: string; count: number }[]
  }
}

export const adminApi = {
  // Auth
  login: (email: string, password: string) =>
    adminAxios.post<{
      success: boolean
      data: { user: AdminUser; tokens: { accessToken: string; refreshToken: string; expiresIn: number } }
    }>('/auth/login', { email, password }),

  // Users
  getUsers: (params: ListParams = {}) =>
    adminAxios.get<{ data: Paginated<AdminUser> }>('/admin/users', { params }),
  getUser: (id: string) =>
    adminAxios.get<{ data: AdminUser }>(`/admin/users/${id}`),
  updateUser: (id: string, data: { role?: string; is_suspended?: boolean; is_verified?: boolean; subscription_tier?: string; subscription_expires_at?: string | null }) =>
    adminAxios.put(`/admin/users/${id}`, data),
  deleteUser: (id: string) =>
    adminAxios.delete(`/admin/users/${id}`),

  // Slips
  getSlips: (params: ListParams = {}) =>
    adminAxios.get<{ data: Paginated<AdminSlip> }>('/admin/slips', { params }),
  getSlip: (id: string) =>
    adminAxios.get<{ data: AdminSlip }>(`/admin/slips/${id}`),
  updateSlipStatus: (id: string, status: SlipStatus) =>
    adminAxios.patch(`/admin/slips/${id}/status`, { status }),

  // Leagues
  getLeagues: () =>
    adminAxios.get<{ data: League[] }>('/admin/leagues'),
  updateLeague: (id: number, data: Partial<League>) =>
    adminAxios.put(`/admin/leagues/${id}`, data),
  createLeague: (data: Partial<League>) =>
    adminAxios.post('/admin/leagues', data),

  // Analytics
  getKPIs: () =>
    adminAxios.get<{ data: KPI }>('/admin/analytics/kpis'),
  getChartData: (metric: string, days: number) =>
    adminAxios.get<{ data: { metric: string; points: ChartPoint[] } }>('/admin/analytics/chart', { params: { metric, days } }),
  getCountries: () =>
    adminAxios.get<{ data: { country_code: string; user_count: number }[] }>('/admin/analytics/countries'),

  // Subscriptions
  getSubscriptions: () =>
    adminAxios.get<{ data: { total_active: number; weekly_subscribers: number; monthly_subscribers: number; annual_subscribers: number; mrr: number; arr: number } }>('/admin/subscriptions'),
  getPlanAnalytics: () =>
    adminAxios.get<{ data: Array<{ plan: string; price: number; interval: string; active_users: number; revenue: number; conversion_rate: number }> }>('/admin/subscriptions/plans'),
  getRevenueChart: (days: number) =>
    adminAxios.get<{ data: Array<{ date: string; weekly: number; monthly: number; annual: number }> }>('/admin/subscriptions/revenue', { params: { days } }),

  // System
  getHealth: () =>
    adminAxios.get<{ data: { status: string; services: Record<string, string>; version: string; maintenance?: boolean } }>('/admin/system/health'),
  getSystemStatus: () =>
    adminAxios.get<{ data: SystemStatus }>('/admin/system/status'),
  flushCache: () =>
    adminAxios.post('/admin/system/cache/flush'),
  reloadML: () =>
    adminAxios.post('/admin/system/ml/reload'),
  setMaintenance: (enabled: boolean) =>
    adminAxios.put('/admin/system/maintenance', { enabled }),

  // Affiliates
  getAffiliates: (params?: { search?: string; tier?: string; status?: string; page?: number; per_page?: number }) =>
    adminAxios.get<{ data: Affiliate[]; meta: { total: number; page: number; per_page: number; total_pages: number } }>('/admin/affiliates', { params }),
  getAffiliateConversions: (params?: { search?: string; status?: string; affiliate_id?: string }) =>
    adminAxios.get<{ data: AffiliateConversion[] }>('/admin/affiliates/conversions', { params }),
  getAffiliatePayouts: (params?: { status?: string }) =>
    adminAxios.get<{ data: AffiliatePayout[] }>('/admin/affiliates/payouts', { params }),
  updateAffiliateStatus: (id: string, status: string) =>
    adminAxios.put(`/admin/affiliates/${id}/status`, { status }),
  updateAffiliateTier: (id: string, tier: string) =>
    adminAxios.put(`/admin/affiliates/${id}/tier`, { tier }),
  processAffiliatePayout: (id: string) =>
    adminAxios.post(`/admin/affiliates/payouts/${id}/process`),
  rejectAffiliatePayout: (id: string) =>
    adminAxios.post(`/admin/affiliates/payouts/${id}/reject`),
  getAffiliateSettings: () =>
    adminAxios.get<{ data: AffiliateSettings }>('/admin/affiliates/settings'),
  updateAffiliateSettings: (settings: Partial<AffiliateSettings>) =>
    adminAxios.put('/admin/affiliates/settings', settings),

  // Referrals
  getReferrals: (params?: { search?: string; status?: string; page?: number; per_page?: number; sort?: string; order?: string }) =>
    adminAxios.get<{ data: Referrer[]; meta: { total: number; page: number; per_page: number; total_pages: number } }>('/admin/referrals', { params }),
  getReferralLeaderboard: () =>
    adminAxios.get<{ data: LeaderboardEntry[] }>('/admin/referrals/leaderboard'),
  getReferralSettings: () =>
    adminAxios.get<{ data: ReferralSettings }>('/admin/referrals/settings'),
  updateReferralSettings: (settings: Partial<ReferralSettings>) =>
    adminAxios.put('/admin/referrals/settings', settings),
  updateReferralStatus: (id: string, status: string) =>
    adminAxios.put(`/admin/referrals/${id}/status`, { status }),

  // SaaS Analytics
  getMRR: (months?: number) =>
    adminAxios.get<{ data: MrrPoint[] }>('/admin/analytics/mrr', { params: { months } }),
  getChurn: (months?: number) =>
    adminAxios.get<{ data: ChurnPoint[] | ChurnAnalytics }>('/admin/analytics/churn', { params: { months } }),
  getLtvCac: (months?: number) =>
    adminAxios.get<{ data: LtvCacPoint[] }>('/admin/analytics/ltv-cac', { params: { months } }),
  getCohortRetention: () =>
    adminAxios.get<{ data: CohortMonth[] }>('/admin/analytics/cohorts'),
  getRevenueSources: () =>
    adminAxios.get<{ data: RevenueSource[] }>('/admin/analytics/revenue-sources'),
  getTrialConversion: () =>
    adminAxios.get<{ data: TrialConversionData }>('/admin/analytics/trial-conversion'),

  // WinBack Campaigns
  getChurnedUsers: (params?: { search?: string; reason?: string; status?: string }) =>
    adminAxios.get<{ data: ChurnedUser[] }>('/admin/winback/churned-users', { params }),
  sendWinBackEmail: (userId: string, templateId: string) =>
    adminAxios.post('/admin/winback/send-email', { user_id: userId, template_id: templateId }),
  markContacted: (userId: string) =>
    adminAxios.post('/admin/winback/mark-contacted', { user_id: userId }),
  getEmailTemplates: () =>
    adminAxios.get<{ data: EmailTemplate[] }>('/admin/winback/templates'),
  updateEmailTemplate: (id: string, data: Partial<EmailTemplate>) =>
    adminAxios.put(`/admin/winback/templates/${id}`, data),
  sendTestEmail: (templateId: string, email: string) =>
    adminAxios.post(`/admin/winback/templates/${templateId}/send-test`, { email }),
  scheduleCampaign: (templateId: string, scheduleDate: string) =>
    adminAxios.post(`/admin/winback/templates/${templateId}/schedule`, { schedule_date: scheduleDate }),
  getCampaignMetrics: () =>
    adminAxios.get<{ data: CampaignMetric[] }>('/admin/winback/campaign-metrics'),

  // Settings
  getFeatureFlags: () =>
    adminAxios.get<{ data: FeatureFlag[] }>('/admin/feature-flags'),
  updateFeatureFlag: (id: string, enabled: boolean) =>
    adminAxios.put(`/admin/feature-flags/${id}`, { enabled }),
  bulkUpdateFeatureFlags: (flags: Array<{ id: string; enabled: boolean }>) =>
    adminAxios.post('/admin/feature-flags/bulk', { flags }),

  // Manual predictions
  getPredictionFixtures: (params: { search?: string; date?: string; status?: string } = {}) =>
    adminAxios.get<{ data: AdminPredictionFixture[] }>('/admin/prediction-fixtures', { params }),
  createPrediction: (data: ManualPredictionInput) =>
    adminAxios.post<{ data: AdminPrediction }>('/admin/predictions', data),
  updatePrediction: (id: string, data: Partial<ManualPredictionInput>) =>
    adminAxios.patch<{ data: AdminPrediction }>(`/admin/predictions/${id}`, data),
  deletePrediction: (id: string) =>
    adminAxios.delete(`/admin/predictions/${id}`),

  // Manual fixtures sync ("Sync Fixtures" button)
  syncFixtures: (job: 'all' | 'fixtures' | 'odds' | 'live' | 'finished' | 'standings' = 'all') =>
    adminAxios.post<{ data: { job: string; status: string; message: string } }>(
      '/admin/fixtures/sync', { job },
    ),
  getFixturesDiag: () =>
    adminAxios.get<{ data: FixturesDiag }>('/admin/fixtures/diag'),

  // Visitor-intent survey + funnel insights
  getInsights: () =>
    adminAxios.get<{ data: Insights }>('/admin/insights'),

  // Prediction confidence-calibration flywheel
  getCalibration: () =>
    adminAxios.get<{ data: Calibration }>('/admin/calibration'),

  // Admin audit log
  getAuditLog: (params: { page?: number; per_page?: number; action?: string; target_type?: string; admin_email?: string } = {}) =>
    adminAxios.get<{ data: { data: AuditEntry[]; meta: { total: number; page: number; per_page: number; total_pages: number } } }>('/admin/audit', { params }),

  // Admin account security
  changeAdminPassword: (currentPassword: string, newPassword: string) =>
    adminAxios.post('/admin/security/change-password', { currentPassword, newPassword }),
  createAdmin: (email: string, password: string, role: 'admin' | 'superadmin') =>
    adminAxios.post<{ data: { id: string; email: string; role: string } }>('/admin/security/admins', { email, password, role }),
  revokeAllSessions: (scope: 'admins' | 'all' = 'all') =>
    adminAxios.post<{ data: { revoked: number; scope: string } }>('/admin/security/revoke-sessions', { scope }),

  // Contact inbox (public contact form)
  getContactMessages: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    adminAxios.get<{ success: boolean; messages: ContactMessage[]; meta: AdminMeta }>('/admin/contact', { params }),
  updateContactStatus: (id: number, status: 'new' | 'read' | 'replied') =>
    adminAxios.patch(`/admin/contact/${id}`, { status }),

  // Refund requests (7-day money-back)
  getRefundRequests: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    adminAxios.get<{ success: boolean; requests: RefundRequest[]; meta: AdminMeta }>('/admin/refunds', { params }),
  decideRefund: (id: number, status: 'approved' | 'rejected', adminNote?: string) =>
    adminAxios.patch(`/admin/refunds/${id}`, { status, adminNote }),

  // Slip review workflow
  generateDraftSlip: (data: { riskLevel?: string; targetOdds?: number; maxLegs?: number; withinHours?: number }) =>
    adminAxios.post<{ data: AdminSlip | null; message?: string }>('/admin/draft-slips', data),
  getDraftSlips: () =>
    adminAxios.get<{ data: AdminSlip[] }>('/admin/draft-slips'),
  publishSlip: (id: string) =>
    adminAxios.post(`/admin/draft-slips/${id}/publish`),
  deleteDraftSlip: (id: string) =>
    adminAxios.delete(`/admin/draft-slips/${id}`),

  // Spinex Arena — coin economy admin surface
  grantArenaCoins: (userId: string, amount: number, reason: string) =>
    adminAxios.post<{ data: { balanceAfter: number } }>('/admin/arena/coins/grant', { userId, amount, reason }),
  freezeArenaWallet: (userId: string, frozen: boolean, reason: string) =>
    adminAxios.post<{ data: { frozen: boolean } }>('/admin/arena/wallets/freeze', { userId, frozen, reason }),
  getArenaEconomy: (days = 7) =>
    adminAxios.get<{
      data: {
        windowDays: number
        coinSupply: number
        flows: { Reason: string; Total: number; Count: number }[]
        faucetTotal: number
        sinkTotal: number
        sinkFaucetRatio: number | null
        funnel: { Event: string; Count: number }[]
      }
    }>('/admin/arena/economy', { params: { days } }),
  runArenaJob: (name: 'settle' | 'boost_select' | 'close_season') =>
    adminAxios.post<{ data: Record<string, unknown> }>(`/admin/arena/jobs/${name}/run`),
}
